import { MouseEvent, ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import styles from "./PlaceForm.module.css";
import inputStyles from "./AddSinglePlaceInput.module.css";
import commonStyles from "./addPlaceCommon.module.css";
import Tabs from "../../../common/Tabs";
import Icon from "../../../common/Icon";
import AddSinglePlaceInput, { DataTemplate } from "./AddSinglePlaceInput";
import { SubContentList } from "./SinglePlaceFormArea";
import AddImageForm from "../../AddImageForm";
import { Field, PlaceCoordsList, PlaceData } from "../../../common/Common";
import DataProvider from "../../../../data_provider/DataProvider";
import { GeocodingQueryParams } from "../../../../data_provider/gateways/RequestGateway";
import LoadingComponent from "../../../common/LoadingSpinner";
import { OpenModalParams } from "../../../modal/Modal";
import ModalButtons from "../../../modal/ModalButtons";
import { usePrevious } from "../../../../hooks/usePrevious";

interface PlaceFormProps {
    data: PlaceData;
    coords: PlaceCoordsList[];
    setValid: Dispatch<SetStateAction<boolean>>;
    setSubContent: Dispatch<SetStateAction<SubContentList | undefined>>;
    setCoords: Dispatch<SetStateAction<PlaceCoordsList[]>>;
    toggleAddMode: Dispatch<SetStateAction<boolean>>;
    newPlaceAddress: string;
    setNewPlaceAddress: Dispatch<SetStateAction<string>>;
    revGeoLoading: boolean;

    agreeNoneAddress?: boolean; // 주소 입력 없이 장소 등록 동의 여부
    setAgreeNoneAddress?: Dispatch<SetStateAction<boolean>>;
    onFormValueChange?: () => void;

    customModal?: { openModal: (param: OpenModalParams) => void; closeModal: () => void };
}
/**
 * @author bakim
 * @date 2024-09-30 09:26:29
 * @remark 단일 장소 등록/수정 화면
 * @param props PlaceFormProps
 * @props
 * @return <PlaceForm>
 */

const PlaceForm = ({ data, ...props }: PlaceFormProps) => {
    const [menusIdx, setMenusIdx] = useState<number>(0);
    const [open, setOpen] = useState<boolean>(false);
    const [coordInfo, setCoordInfo] = useState({ coords: [] as string[], address: data.address.value ?? "" }); // 좌표 정보
    const [placeName, setPlaceName] = useState<string>(data.placeName.value ?? "");

    const [validation, setValidation] = useState<{ [key: string]: boolean }>({
        [data.address.fieldKey]: data.XID ? true : false,
        [data.placeName.fieldKey]: data.XID ? true : false,
        //[data.category.fieldKey]: data.XID ? true : false,
        [data.category.fieldKey]: true,
    });

    const addressRef = useRef<HTMLInputElement>(null);

    const previousAddresss = useRef<string>("");

    const menus = ["주소입력", "지도위치"];
    const prevMenusIdx = usePrevious(menusIdx); // 이전에 선택된 메뉴 기억

    useEffect(() => {
        if (data.XID) {
            props.setNewPlaceAddress(data.address.value);
            previousAddresss.current = data.address.value;
        }
    }, []);

    useEffect(() => {
        if (props.coords.length && 1 in props.coords[0] && props.coords[0][1].coords) {
            const _coords = props.coords[0][1].coords.map((item: number) => String(item));
            setCoordInfo({ ...coordInfo, coords: _coords });
        }
    }, [props.coords]);

    useEffect(() => {
        setValidation({ ...validation, [data.category.fieldKey]: data.category.value !== "" });
    }, [data.category.value]);

    useEffect(() => {
        // address, placeName, category에 유효하지 않은 값이 없으면 통과
        if (validation) {
            const valid = Object.values(validation).filter((value: boolean) => !value);
            props.setValid(valid?.length === 0);
        }
        onChangeChildElement("dummy");
    }, [validation]);

    useEffect(() => {
        // props.toggleAddMode(menusIdx ? true : false);
        props.toggleAddMode(!!menusIdx);
        onChangeChildElement("dummy");

        // 주소입력 방법이 바뀔 때마다 주소 발리데이션 초기화 | 250829 | smyun
        // validation false로 변경
        setValidation((prev) => ({ ...prev, [data.address.fieldKey]: false }));

        // 좌표값 비우기
        if (coordInfo.coords.length > 0) {
            setCoordInfo((prev) => ({
                ...prev,
                coords: [], // 좌표값 비우기(주소입력으로 찾는 경우에도 setCoordInfo에 값이 채워짐)
                address: "", // 주소값 (input에 "미등록 주소지" 표시 지우기)
            }));
        }

        if (prevMenusIdx === 0 && menusIdx === 1) {
            /* 주소입력 → 지도위치 */

            // 로직 상 true가 되는 경우 해제
            props.setNewPlaceAddress(""); // 입력된 주소값 비우기
        } else if (prevMenusIdx === 1 && menusIdx === 0) {
            /* 지도위치 → 주소입력 */

            // 위치로 입력된 상황 해지
            props.setAgreeNoneAddress?.(false);
            props.setCoords([]);
        }
    }, [menusIdx]);

    useEffect(() => {
        // 좌표(지도위치)로 추가 하는 경우
        if (menusIdx === 1) {
            if (coordInfo.coords.length > 0) {
                setValidation({ ...validation, [data.address.fieldKey]: true });
            } else {
                setValidation({ ...validation, [data.address.fieldKey]: false });
            }
        } else {
            if (props.newPlaceAddress !== "") {
                setValidation({ ...validation, [data.address.fieldKey]: true });
            } else {
                setValidation({ ...validation, [data.address.fieldKey]: false });
            }
        }

        if (props.newPlaceAddress !== "미등록 주소지") {
            previousAddresss.current = data.address.value;
        }
        onChangeChildElement("dummy");
    }, [props.newPlaceAddress]);

    useEffect(() => {
        if (props.agreeNoneAddress) {
            setCoordInfo((prev) => ({ ...prev, address: "미등록 주소지" }));
            props.setNewPlaceAddress("미등록 주소지");
            setValidation((prev) => ({
                ...prev,
                [data.category.fieldKey]: true,
                [data.address.fieldKey]: true,
            }));
        } else {
            props.setNewPlaceAddress(previousAddresss.current);
        }
        onChangeChildElement("dummy");
    }, [props.agreeNoneAddress]);

    /**
     * @description 데이터 템플릿 및 필수 여부에 따른 유효성 검증
     * @param item
     * @returns value
     */
    const inputValidation = (item: Field) => {
        let valid = false;

        switch (item.template) {
            case DataTemplate.req:
                valid = new RegExp(/^.{1,254}$/).test(item.value) ? true : false;
                break;
            case DataTemplate.call:
                valid = new RegExp(/^\d{9,12}$/).test(item.value) || !item.value ? true : false;
                break;
            default:
                valid = new RegExp(/^.{0,254}$/).test(item.value) ? true : false;
                break;
        }
        setValidation({ ...validation, [item.fieldKey]: valid });
        return valid;
    };

    const reqGeocoding = async () => {
        console.log("reqGeocoding");

        if (!data || !addressRef.current) return;

        let flag = false;

        if (addressRef.current.value) {
            const param: GeocodingQueryParams = {
                index: "0",
                query: addressRef.current.value,
            };

            const response = await DataProvider.Geocoding(param);

            if (response.flag) {
                const result = response.result;
                props.setCoords([
                    {
                        1: {
                            category: data.category.value,
                            label: placeName,
                            coords: result.coords,
                        },
                    },
                ]);

                setCoordInfo({
                    coords: result.coords.map((value: number) => {
                        return value.toString();
                    }),
                    address: result.address,
                });
                props.setNewPlaceAddress(result.address);

                // 마커 이동
            } else {
                if (props.customModal) {
                    const params = {
                        title: "주소 찾기 실패",
                        children: <>주소를 찾을 수 없습니다.</>,
                        modalButtons: (
                            <ModalButtons
                                buttonCount={1}
                                buttons={[
                                    {
                                        label: "확인",
                                        color: "Primary",
                                        onClick: () => {
                                            props.customModal!.closeModal();
                                        },
                                    },
                                ]}
                            />
                        ),
                        disableOverlayClose: true,
                    };
                    props.customModal.openModal(params);
                } else {
                    alert("주소를 찾을 수 없습니다.");
                }
            }

            flag = response.flag;
        }

        // setValidation({ ...validation, [data.address.fieldKey]: flag });
    };

    const selectCategory = () => {
        props.setSubContent(SubContentList.categoryList);
        onChangeChildElement("dummy");
    };

    const openExtraData = () => {
        setOpen(!open);
    };

    const onChangeAddressInput = (e: ChangeEvent<HTMLInputElement>) => {
        //주소 텝을 열고 다시 입력하면 위치로 입력된 상황을 해지한다. // menusIdx 바뀔 때 처리하도록 이동 | 250901 | smyun
        // props.setAgreeNoneAddress!(false);
        // props.setCoords([]);

        const target = e.currentTarget;
        setCoordInfo({ ...coordInfo, address: target.value });

        props.setNewPlaceAddress("");
        setValidation({ ...validation, [data.address.fieldKey]: false });
        onChangeChildElement("dummy");
    };

    const onClickAddressInput = (e: MouseEvent<HTMLInputElement>) => {};

    const onChangePlaceName = (value: string) => {
        setPlaceName(value);
        onChangeChildElement("dummy");
    };

    // 하위 컴포넌트드의 값이 변경되는 시점을 잡기위한 함수.
    const onChangeChildElement = (value: string) => {
        if (props.onFormValueChange) {
            props.onFormValueChange();
        }
    };

    return (
        <>
            <div className={commonStyles.sub_title}>
                <div className={styles.title}>필수 정보</div>
            </div>
            <AddSinglePlaceInput item={data.placeName} requirement={true} checkValidation={inputValidation} onChange={onChangePlaceName} />
            {data?.category &&
                [data.category.fieldKey, data.category.fieldName, data.category.value].some((v) =>
                    typeof v === "string" ? v.trim() !== "" : v != null
                ) && (
                    <div className={inputStyles.item}>
                        <div className={inputStyles.header}>
                            <Icon name="check" color={validation && validation[data.category.fieldKey] ? "default" : "lightGrey"} />
                            <label className={inputStyles.label}>{data.category.fieldName}</label>
                        </div>
                        <div className={`${inputStyles.input}`} onClick={selectCategory}>
                            <input name={data.category.fieldKey} placeholder="카테고리 선택" value={data.category.value ?? undefined} readOnly />
                            <div className={`${inputStyles.float} ${inputStyles.icon}`}>
                                <Icon name="arrowForward" color="lightGrey" size="small" />
                            </div>
                        </div>
                    </div>
                )}

            <div className={`${styles.location} ${inputStyles.item}`}>
                <div className={inputStyles.header}>
                    <Icon name="check" color={validation && validation[data!.address.fieldKey] ? "default" : "lightGrey"} />
                    <label className={inputStyles.label}>{data.address.fieldName}</label>
                </div>
                <div className={styles.tab}>
                    <Tabs options={menus} onItemClick={setMenusIdx} tabMenuIdx={menusIdx} />
                </div>
                <div className={inputStyles.item} style={{ position: "relative" }}>
                    <input id="geometry" type="hidden" name="geometry" value={coordInfo.coords.length ? coordInfo.coords.join(",") : ""} />
                    {/* {(<div className={`${styles.addressLoading}`}><LoadingComponent /></div>)} */}
                    {props.revGeoLoading ? (
                        <div className={`${styles.addressLoading}`}>
                            <LoadingComponent />
                        </div>
                    ) : (
                        ""
                    )}
                    {menusIdx ? (
                        <>
                            <input
                                ref={addressRef}
                                type="hidden"
                                name={data.address.fieldKey}
                                value={props.newPlaceAddress != "" ? props.newPlaceAddress : coordInfo.address}
                            />
                            <div className={styles.desc}>
                                <span className={styles.text}>{"지도 위의 마커 아이콘을 원하는 위치로 움직이세요."}</span>
                                <span className={styles.text}>{"주소입력을 클릭하면 위치가 초기화됩니다."}</span>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={`${styles.addressInputContainer}`}>
                                <div className={`${inputStyles.input}`}>
                                    <input
                                        onClick={onClickAddressInput}
                                        className={`${inputStyles.addressInput}`}
                                        name={data.address.fieldKey}
                                        ref={addressRef}
                                        placeholder="주소입력"
                                        value={
                                            props.agreeNoneAddress
                                                ? "미등록 주소지"
                                                : props.newPlaceAddress !== ""
                                                ? props.newPlaceAddress
                                                : coordInfo.address
                                        }
                                        onChange={onChangeAddressInput}
                                        disabled={props.revGeoLoading}
                                        //readOnly={props.agreeNoneAddress}
                                    />
                                    <div
                                        className={`${inputStyles.float} ${inputStyles.icon} ${props.revGeoLoading ? inputStyles.noneClick : ""}`}
                                        onClick={props.revGeoLoading ? undefined : reqGeocoding}
                                    >
                                        <Icon name="send" cursor={props.revGeoLoading ? "default" : "pointer"} />
                                    </div>
                                </div>
                                {/* {props.revGeoLoading ? (<div className={`${styles.addressLoading}`}><LoadingComponent /></div>) : ""} */}
                            </div>
                            <div className={styles.text}>
                                {"아래의 형태로 입력하세요."}
                                <div className={styles.title}> &lt;지번 주소&gt; </div>
                                <div>
                                    {"광역시/도 + 시/군/구 + 읍/면/동 + (리) + 번지 + 상세주소  \n 예) 서울특별시 관악구 신림동 산56-1, 서울대학교"}
                                </div>
                                <div className={styles.title}>&lt;도로명주소&gt;</div>
                                <div>{"광역시/도 + 시/군/구 + 읍/면/(동) + 도로명 + 건물번호  \n 예) 서울대학교 관악구 관악로 1, 서울대학교"}</div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className={commonStyles.sub_title}>
                <div className={styles.title}>주요 정보</div>
            </div>
            {!data.XID && <AddImageForm limit={10} />}
            {data.mainData.length > 0 &&
                data.mainData.map((item: Field, index: number) => {
                    return (
                        <AddSinglePlaceInput
                            key={`maindata_${index}`}
                            item={item}
                            requirement={false}
                            onChange={onChangeChildElement}
                            checkValidation={inputValidation}
                        />
                    );
                })}
            {data.extraData && data.extraData.length > 0 && (
                <>
                    <div className={`${commonStyles.sub_title} ${commonStyles.pointer}`} onClick={openExtraData}>
                        <div className={styles.title}> 상세 정보</div>
                        <div className={inputStyles.icon}>
                            <Icon name={open ? "arrowUp" : "arrowDown"} color="darkGrey" />
                        </div>
                    </div>
                    {open &&
                        data.extraData.map((item: Field, index: number) => {
                            return (
                                <AddSinglePlaceInput
                                    key={`extradata_${index}`}
                                    item={item}
                                    requirement={false}
                                    onChange={onChangeChildElement}
                                    checkValidation={inputValidation}
                                />
                            );
                        })}
                </>
            )}
        </>
    );
};

export default PlaceForm;
